import type { PriceItem,
  AuditRow,
  CaseRow,
  CaseTemplate,
  CaseType,
  CustomForm,
  CustomFormSummary,
  DocContent,
  DocContext,
  DocRecord,
  DocTemplate,
  DocTemplateRef,
  DeletedOpEntry,
  Funding,
  IolCategory,
  AdminStats,
  AdminUser,
  Appointment,
  ApptStatus,
  ApptBlock,
  ApptWindow,
  Cabinet,
  CabinetKind,
  IolModel,
  Service,
  IolMove,
  IolStock,
  IolSuggestion,
  StockOrder,
  StockBatch,
  StockDoc,
  StockDocInput,
  StockPosition,
  UpdateStatus,
  EntryMaterial,
  DayMaterial,
  LensState,
  AiAdmin,
  AiCitation,
  AiConclusion,
  AiFillResult,
  AiFormField,
  AiRunRef,
  AiSource,
  AiStatus,
  JoinMode,
  Meta,
  OpDay,
  OpEntry,
  OpEntryForCase,
  OpList,
  QueueCat,
  ReminderInput,
  ReminderView,
  FileKind,
  OpStatus,
  PayState,
  OperProfile,
  Patient,
  SessionUser,
  Temp,
  PrevOp,
} from "./types";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: "same-origin",
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });
const put = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: "PUT", body: body === undefined ? undefined : JSON.stringify(body) });
const del = <T>(path: string) => request<T>(path, { method: "DELETE" });

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "" || v === false) continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

/** The body both create and update take: the header plus its complete row list. */
export interface CaseTemplateInput {
  name: string;
  case_type: CaseType;
  about: string;
  oper_name: string;
  oper2_name: string;
  iol: boolean;
  iol_about: string;
  vid_an: string;
  zav: string;
  ad: string;
  chss: string;
  chdd: string;
  sort_order: number;
  items: { docs_id: string; qty: number; form_tpl_id: number | null; checked: boolean }[];
}

/** The body both create and update take for a «профиль операции». */
export interface OperProfileInput {
  oper_name: string;
  code: string;
  note: string;
  items: {
    docs_id: string;
    field: string;
    slot: string;
    temp_id: number | null;
    form_tpl_id: number | null;
  }[];
}

/** Everything a row of the operations register is created and edited with. */
export interface OpEntryInput {
  op_date: string;
  surgeon: string;
  /** «Осмотр/расчёт»; пустое — подставится автор первичного осмотра. */
  osm_fio: string;
  category: string;
  eye: string;
  oper_type: string;
  /** 0 — операция; 1 — консультация, 2 — п/о (послеоперационный осмотр) — что именно, пишется в oper_type. */
  is_consult: 0 | 1 | 2;
  /** Оплата записи: '' | oms | mezhter | paid. */
  funding: string;
  iol_model: string;
  iol_diop: string;
  iol_cyl: string;
  /** Артифакия (только у операций с имплантацией): что уже стоит у пациента. */
  art_iol_model: string;
  art_iol_diop: string;
  art_eye: string;
  comment: string;
  /** Категория очереди ОМС — только у строк очереди; не прислали — не меняется. */
  queue_cat_id?: number | null;
  /**
   * Позиция склада, выбранная «из склада»: сервер резервирует именно её (и
   * позицию с вариантом исполнения тоже), а не первую похожую.
   */
  iol_stock_pick?: number | null;
}

export interface PatientFilters {
  page?: number;
  query?: string;
  /** Строка поиска — диагноз: искать в документах пациента, а не в карточке. */
  diag?: boolean;
  okt?: boolean;
  doctor?: string;
  month?: number;
  year?: number;
}

/** Окно приёма: кабинет, дата, «с … до», интервал и время между приёмами. */
export interface ApptWindowInput {
  cabinet_id: number;
  doctor: string;
  date: string;
  time_from: string;
  time_to: string;
  slot_minutes: number;
  gap_minutes: number;
  comment: string;
}

/** Заблокированное время: кабинет, дата, «с … до», причина. */
export interface ApptBlockInput {
  cabinet_id: number;
  date: string;
  time_from: string;
  time_to: string;
  reason: string;
}

export const api = {
  /* auth */
  login: (login: string, pass: string) =>
    post<{ user: SessionUser }>("/auth/login", { login, pass }),
  logout: () => post<{ ok: true }>("/auth/logout"),
  me: () => request<{ user: SessionUser }>("/auth/me"),

  /* reference data */
  meta: () => request<Meta>("/meta"),
  doctors: () => request<{ doctors: string[] }>("/temps/doctors"),

  /** Диктовка: the recording goes up as raw bytes, only the text comes back.
   *  `headers` here replaces the JSON default — the body is a Blob, not JSON. */
  /** Load the recogniser ahead of the first press — see AssistantPanel. */
  warmupAsr: () => post<{ loaded: boolean; loading: boolean }>("/asr/warmup"),
  /** Запущен ли GigaAM (app/asr) — без него микрофон не нужен, текст можно вставить. */
  asrHealth: () => request<{ ok: boolean; loaded?: boolean; model?: string }>("/asr/health"),

  /* ИИ-помощник — собственная модель клиники (api/src/model) */
  aiStatus: () => request<AiStatus>("/ai/status"),
  aiFill: (docsId: string, text: string, fields: AiFormField[]) =>
    post<AiFillResult>("/ai/fill", { docs_id: docsId, text, fields }),
  aiConclude: (docsId: string, fields: AiFormField[], patId: number | null) =>
    post<AiConclusion>("/ai/conclude", { docs_id: docsId, fields, pat_id: patId }),
  aiCompleteStatus: (docsId: string, fields: AiFormField[]) =>
    post<{ fields: AiFillResult["fields"]; run_id: number | null }>("/ai/complete-status", { docs_id: docsId, fields }),
  aiPassage: (chunkId: number) =>
    request<{ passage: AiCitation & { text: string; category: string } }>(`/ai/passage/${chunkId}`),

  transcribe: (audio: Blob) =>
    request<{ text: string }>("/asr/transcribe", {
      method: "POST",
      body: audio,
      headers: { "Content-Type": audio.type || "audio/webm" },
    }),

  /* patients */
  patients: (f: PatientFilters) =>
    request<{
      patients: Patient[];
      page: number;
      perPage: number;
      total: number;
      pages: number;
    }>(`/patients${qs({ ...f, okt: f.okt ? 1 : undefined, diag: f.diag ? 1 : undefined })}`),
  patient: (id: number) => request<{ patient: Patient }>(`/patients/${id}`),
  checkDuplicate: (fio: string, bdate: string, except?: number) =>
    post<{
      duplicate: boolean;
      pat_id: number | null;
      candidates: { pat_id: number; fio: string; bdate: string | null; phone: string }[];
    }>("/patients/check-duplicate", {
      except,
      fio,
      bdate,
    }),
  /** Объединить карточки: всё из fromId переезжает в intoId, fromId удаляется. */
  mergePatient: (fromId: number, intoId: number) =>
    post<{ ok: true; moved: { cases: number; entries: number; appts: number }; into: { pat_id: number; fio: string } }>(
      `/patients/${fromId}/merge`,
      { into: intoId }
    ),
  createPatient: (body: Record<string, unknown>) =>
    post<{ pat_id: number }>("/patients", body),
  updatePatient: (id: number, body: Record<string, unknown>) =>
    put<{ ok: true }>(`/patients/${id}`, body),
  deletePatient: (id: number) => del<{ ok: true }>(`/patients/${id}`),

  /* cases */
  cases: (patId: number) => request<{ cases: CaseRow[] }>(`/cases${qs({ pat_id: patId })}`),
  caseOne: (id: number) => request<{ case: CaseRow }>(`/cases/${id}`),
  createCase: (body: Record<string, unknown>) => post<{ case_id: number }>("/cases", body),
  updateCase: (id: number, body: Record<string, unknown>) =>
    put<{ ok: true }>(`/cases/${id}`, body),
  deleteCase: (id: number) => del<{ ok: true }>(`/cases/${id}`),
  uniteCases: (caseSelected: number, cases: number[]) =>
    post<{ ok: true; kept: number; merged: number[] }>("/cases/unite", {
      case_selected: caseSelected,
      cases,
    }),

  /* documents */
  docContext: (params: {
    pat_id: number;
    case_id?: number | null;
    doc_id?: number | null;
    sources?: string[];
  }) =>
    request<DocContext>(
      `/docs/context${qs({
        pat_id: params.pat_id,
        case_id: params.case_id ?? undefined,
        doc_id: params.doc_id ?? undefined,
        sources: params.sources?.length ? params.sources.join(",") : undefined,
      })}`
    ),
  doc: (id: number) => request<{ doc: DocRecord }>(`/docs/${id}`),
  otherDocs: (patId: number, docsId?: string) =>
    request<{
      groups: {
        case_id: number;
        label: string;
        docs: { doc_id: number; docs_id: string; created_at: string; label: string }[];
      }[];
    }>(`/docs/other${qs({ pat_id: patId, docs_id: docsId })}`),
  /** `aiRuns` — работы ИИ-помощника в документе: модель сравнит их с сохранённым. */
  createDoc: (caseId: number, docsId: string, content: DocContent, aiRuns?: AiRunRef[]) =>
    post<{ doc_id: number }>("/docs", { case_id: caseId, docs_id: docsId, content, ai_runs: aiRuns?.length ? aiRuns : undefined }),
  /**
   * `linked` — документы того же стационарного случая, где связанные поля
   * (диагноз, VIS/ВГД, статус глаз…) получили новое значение вслед за этим.
   */
  updateDoc: (docId: number, content: DocContent, aiRuns?: AiRunRef[]) =>
    put<{ ok: true; linked?: { doc_id: number; docs_id: string; fields: string[] }[] }>(
      `/docs/${docId}`,
      { content, ai_runs: aiRuns?.length ? aiRuns : undefined }
    ),
  deleteDocs: (docIds: number[]) =>
    post<{ ok: true; deleted: number }>("/docs/delete", { doc_ids: docIds }),

  /* whole-form saved templates */
  docTemplates: (docsId: string) =>
    request<{ templates: DocTemplate[] }>(`/docs/templates/${encodeURIComponent(docsId)}`),
  docTemplate: (docsId: string, docId: number) =>
    request<{ template: DocRecord }>(
      `/docs/templates/${encodeURIComponent(docsId)}/${docId}`
    ),
  createDocTemplate: (body: {
    docs_id: string;
    case_id: number | null;
    template_name: string;
    content: DocContent;
  }) => post<{ doc_id: number; template_name: string }>("/docs/templates", body),
  deleteDocTemplate: (docId: number) => del<{ ok: true }>(`/docs/templates/${docId}`),

  /* «шаблоны истории» — named document sets for a whole case */
  caseTemplates: (caseType?: CaseType) =>
    request<{ templates: CaseTemplate[] }>(`/case-templates${qs({ case_type: caseType })}`),
  createCaseTemplate: (body: CaseTemplateInput) => post<{ tpl_id: number }>("/case-templates", body),
  updateCaseTemplate: (tplId: number, body: CaseTemplateInput) =>
    put<{ ok: true }>(`/case-templates/${tplId}`, body),
  deleteCaseTemplate: (tplId: number) => del<{ ok: true }>(`/case-templates/${tplId}`),
  /** Every saved whole-form template in one request, for the wizard's per-row picker. */
  allDocTemplates: () => request<{ templates: DocTemplateRef[] }>("/docs/templates"),

  /* «профили операций» — what picking an операция pre-fills */
  operProfiles: () => request<{ profiles: OperProfile[] }>("/oper-profiles"),
  createOperProfile: (body: OperProfileInput) =>
    post<{ prof_id: number }>("/oper-profiles", body),
  updateOperProfile: (profId: number, body: OperProfileInput) =>
    put<{ ok: true }>(`/oper-profiles/${profId}`, body),
  deleteOperProfile: (profId: number) => del<{ ok: true }>(`/oper-profiles/${profId}`),

  /* импортированные файлы случая (кнопка «Импорт») */
  /** name — как назвать файл в случае (по умолчанию — исходное имя); расширение сервер ставит по содержимому. */
  uploadDocFile: (caseId: number, file: File, name = file.name) =>
    request<{ doc_id: number; name: string; ext: string; size: number; kind: FileKind }>(
      `/doc-files${qs({ case_id: caseId, name })}`,
      { method: "POST", body: file, headers: { "Content-Type": "application/octet-stream" } }
    ),
  /** DOCX — страницей, DOC/RTF/ODT/TXT — текстом. */
  docFilePreview: (docId: number) =>
    request<{ name: string; kind: "html" | "text"; body: string }>(`/doc-files/${docId}/preview`),
  /** Адрес самого файла: показать в окне (с именем — его видно в просмотрщике PDF) или (download) сохранить. */
  docFileUrl: (docId: number, download = false, name = "") =>
    `/api/doc-files/${docId}${name ? `/view/${encodeURIComponent(name)}` : ""}${download ? "?download=1" : ""}`,

  /* учёт оперативных вмешательств — операционные списки */
  opLists: () => request<{ lists: OpList[] }>("/op-lists"),
  createOpList: (body: {
    code: string;
    name: string;
    year: number;
    writes_off_iol: boolean;
    checks_duplicates: boolean;
    free_oper_type: boolean;
    sort_order: number;
  }) => post<{ list_id: number }>("/op-lists", body),
  opEntries: (
    listId: number,
    f: { q?: string; from?: string; to?: string; day_id?: number | "" } = {}
  ) => request<{ entries: OpEntry[] }>(`/op-lists/entries${qs({ list_id: listId, ...f })}`),
  createOpEntry: (
    listId: number,
    patId: number,
    body: OpEntryInput,
    dayId?: number | null,
    /** Пациент вне системы — вместо pat_id (который тогда 0). */
    ext?: { fio: string; bdate: string | null; phone: string } | null
  ) =>
    post<{ entry_id: number; iol_manual: boolean; stock_warning: string | null }>(
      "/op-lists/entries",
      { list_id: listId, pat_id: patId, day_id: dayId ?? null, ext: ext ?? undefined, ...body }
    ),

  /* операционные дни */
  opDays: (listId: number, all = false) =>
    request<{ days: OpDay[] }>(`/op-lists/days${qs({ list_id: listId, all: all ? 1 : undefined })}`),
  opDayLoad: (dayId: number) =>
    request<{ day: OpDay; surgeons: { surgeon: string; n: number }[] }>(
      `/op-lists/days/${dayId}/load`
    ),
  openOpDay: (body: {
    list_id: number;
    op_date: string;
    /** Коды через «+»: "oms", "oms+paid", … */
    funding: string;
    funding_other: string;
    planned: number;
    comment: string;
    folded: boolean;
  }) => post<{ day_id: number; label: string }>("/op-lists/days", body),
  updateOpDay: (
    dayId: number,
    body: {
      op_date: string;
      funding: string;
      funding_other: string;
      planned: number;
      comment: string;
      folded: boolean;
    }
  ) => put<{ ok: true }>(`/op-lists/days/${dayId}`, body),
  /** Порядок строк дня после перетаскивания — id в новом порядке. */
  sortOpEntries: (ids: number[]) => post<{ ok: true }>("/op-lists/entries/sort", { ids }),
  /** Явные места строк (с дырками): {entry_id, sort_order} — задеты только они. */
  sortOpPositions: (positions: { entry_id: number; sort_order: number }[]) =>
    post<{ ok: true }>("/op-lists/entries/sort", { positions }),
  /** Единое время явки для отмеченных строк; "" — снять. */
  setComeTime: (ids: number[], time: string) =>
    post<{ ok: true; updated: number }>("/op-lists/entries/come-time", { ids, time }),
  closeOpDay: (dayId: number) => del<{ ok: true }>(`/op-lists/days/${dayId}`),
  /** «Перенести на другую дату» — тот же список, другой день. */
  rescheduleOpEntry: (entryId: number, dayId: number) =>
    post<{ ok: true; op_date: string }>(`/op-lists/entries/${entryId}/reschedule`, { day_id: dayId }),
  /** Что оперсписок знает про случай этого пациента на эти даты. */
  opEntryForCase: (patId: number, dateIn: string, dateOut?: string) =>
    request<{ found: boolean; entry: OpEntryForCase | null }>(
      `/op-lists/for-case${qs({ pat_id: patId, date_in: dateIn, date_out: dateOut })}`
    ),
  updateOpEntry: (entryId: number, body: OpEntryInput) =>
    put<{ ok: true; iol_manual: boolean }>(`/op-lists/entries/${entryId}`, body),
  /** Одна ячейка листа ожидания — не вся строка: см. PATCH /entries/:id/plan. */
  planOpEntry: (
    entryId: number,
    patch: Partial<{
      pay_state: PayState;
      pay_paid: number;
      pay_left: number;
      lens_state: LensState;
      plan_day_id: number | null;
      status_manual: "" | OpStatus;
      diop_confirmed: boolean;
      comment: string;
    }>
  ) =>
    request<{ ok: true; status: OpStatus; plan_date: string | null }>(
      `/op-lists/entries/${entryId}/plan`,
      { method: "PATCH", body: JSON.stringify(patch) }
    ),
  /** Опердни, на которые можно записать из листа ожидания — все списки, вперёд. */
  planDays: () => request<{ days: OpDay[] }>("/op-lists/plan-days"),
  /**
   * Перенос в другой список. В оперирующий — только на открытый опердень
   * (day_id), в очередь ОМС — только в категорию со свободным местом.
   */
  moveOpEntry: (entryId: number, listId: number, to: { day_id?: number | null; queue_cat_id?: number | null } = {}) =>
    post<{ ok: true; iol_manual: boolean; day_id: number | null; op_date: string | null }>(
      `/op-lists/entries/${entryId}/move`,
      { list_id: listId, ...to }
    ),
  /* напоминания по строкам оперсписка */
  /** Открытые напоминания — сработавшие и ждущие, по дате создания. */
  reminders: () => request<{ reminders: ReminderView[]; due: number }>("/op-reminders"),
  /** Сколько сработало — число на колокольчике. */
  reminderCount: () => request<{ due: number; open: number }>("/op-reminders/count"),
  reminder: (remId: number) => request<{ reminder: ReminderView }>(`/op-reminders/${remId}`),
  /** Поставить строке напоминание; если открытое уже есть — это его правка. */
  createReminder: (entryId: number, body: ReminderInput) =>
    post<{ rem_id: number; reminder: ReminderView }>("/op-reminders", { entry_id: entryId, ...body }),
  updateReminder: (remId: number, body: ReminderInput) =>
    put<{ reminder: ReminderView }>(`/op-reminders/${remId}`, body),
  /** «Выполнено» — у всех; note — что сделали. 409, если уже выполнено кем-то. */
  reminderDone: (remId: number, note = "") => post<{ ok: true }>(`/op-reminders/${remId}/done`, { note }),
  deleteReminder: (remId: number) => del<{ ok: true }>(`/op-reminders/${remId}`),
  /** Карточка открыта — отметка «открыта у …»; held — чья отметка уже стоит. */
  holdReminder: (remId: number) =>
    post<{ held: { user_id: number; fio: string; at: string } | null }>(`/op-reminders/${remId}/hold`),
  releaseReminder: (remId: number) => del<{ ok: true }>(`/op-reminders/${remId}/hold`),
  /* категории очереди ОМС */
  queueCats: (listId: number) => request<{ cats: QueueCat[] }>(`/op-lists/queue-cats${qs({ list_id: listId })}`),
  createQueueCat: (body: { list_id: number; name: string; slots: number }) =>
    post<{ cat_id: number }>("/op-lists/queue-cats", body),
  updateQueueCat: (catId: number, body: { name: string; slots: number }) =>
    put<{ ok: true }>(`/op-lists/queue-cats/${catId}`, body),
  deleteQueueCat: (catId: number) => del<{ ok: true }>(`/op-lists/queue-cats/${catId}`),
  deleteOpEntry: (entryId: number) => del<{ ok: true }>(`/op-lists/entries/${entryId}`),
  /** The lens the patient's первичный осмотр worked out for this eye. */
  /** Прежние имплантации пациента — подсказка артифакии в форме записи. */
  prevOps: (patId: number) => request<{ ops: PrevOp[] }>(`/op-lists/prev-ops${qs({ pat_id: patId })}`),
  iolSuggest: (patId: number, eye: string) =>
    request<IolSuggestion>(`/op-lists/iol-suggest${qs({ pat_id: patId, eye })}`),
  /* admin-only clean-up */
  deletedOpEntries: () => request<{ entries: DeletedOpEntry[] }>("/op-lists/deleted"),
  restoreOpEntry: (entryId: number) => post<{ ok: true }>(`/op-lists/entries/${entryId}/restore`),
  /** Пациенту вне системы — карточка из картотеки. */
  linkOpEntry: (entryId: number, patId: number) =>
    post<{ ok: true; pat_id: number; fio: string }>(`/op-lists/entries/${entryId}/link`, { pat_id: patId }),

  /* склад ИОЛ */
  iolCategories: () => request<{ categories: IolCategory[] }>("/iol/categories"),
  createIolCategory: (name: string) => post<{ cat_id: number }>("/iol/categories", { name }),
  updateIolCategory: (catId: number, name: string) =>
    put<{ ok: true }>(`/iol/categories/${catId}`, { name }),
  deleteIolCategory: (catId: number) => del<{ ok: true }>(`/iol/categories/${catId}`),
  /** «Операция выполнена» — списывает линзу; undo возвращает её компенсацией. */
  /* прайс-лист */
  priceList: () =>
    request<{ items: PriceItem[]; approved: string; can_edit: boolean }>("/price"),
  createPriceItem: (kind: PriceItem["kind"], afterId: number | null) =>
    post<{ item_id: number; items: PriceItem[] }>("/price", { kind, after_id: afterId }),
  updatePriceItem: (itemId: number, patch: Partial<Omit<PriceItem, "item_id" | "sort_order">>) =>
    put<{ ok: true; item: PriceItem }>(`/price/${itemId}`, patch),
  deletePriceItem: (itemId: number) => del<{ ok: true }>(`/price/${itemId}`),
  movePriceItem: (itemId: number, dir: -1 | 1) =>
    post<{ ok: true; items: PriceItem[] }>(`/price/${itemId}/move`, { dir }),
  setPriceApproved: (text: string) => put<{ ok: true }>("/price/approved", { text }),

  /** lens — что списано (или вернулось); lens_note — почему списывать было нечего. */
  markOperated: (entryId: number, undo = false) =>
    post<{ ok: true; operated: boolean; lens: string | null; lens_note: string | null }>(
      `/op-lists/entries/${entryId}/operated`,
      { undo }
    ),
  iolStock: (f: { q?: string; low?: boolean } = {}) =>
    request<{ stock: IolStock[] }>(`/iol/stock${qs({ q: f.q, low: f.low ? 1 : undefined })}`),
  iolModels: () => request<{ models: IolModel[] }>("/iol/models"),
  createIolModel: (body: {
    category_id: number | null;
    maker: string;
    name: string;
    kind?: "iol" | "material";
    unit?: string;
    variant_label?: string;
    /** Интервалы диоптрий — на каждую диоптрию заводится позиция с нулём. */
    ranges?: { from: string; to: string; step: string }[];
  }) => post<{ model_id: number; generated?: number }>("/iol/models", body),
  /** Дозавести интервалы диоптрий к существующей модели. */
  addIolRanges: (modelId: number, ranges: { from: string; to: string; step: string }[]) =>
    post<{ ok: true; generated: number }>(`/iol/models/${modelId}/ranges`, { ranges }),
  updateIolModel: (
    modelId: number,
    body: { category_id: number | null; maker: string; name: string; kind?: "iol" | "material"; unit?: string; variant_label?: string }
  ) => put<{ ok: true }>(`/iol/models/${modelId}`, body),
  deleteIolModel: (modelId: number) => del<{ ok: true }>(`/iol/models/${modelId}`),
  createIolStock: (body: {
    category_id: number | null;
    model: string;
    diop: string;
    cyl: string;
    variant?: string;
    maker: string;
    note: string;
    min_qty: number;
    expiry?: string;
    /** Позиция под моделью: имя/производитель/категория берутся из неё. */
    model_id?: number;
  }) => post<{ stock_id: number; restored?: boolean }>("/iol/stock", body),
  updateIolStock: (
    stockId: number,
    body: {
      category_id: number | null;
      model: string;
      diop: string;
      cyl: string;
      variant?: string;
      maker: string;
      note: string;
      min_qty: number;
      expiry?: string;
    }
  ) => put<{ ok: true }>(`/iol/stock/${stockId}`, body),
  deleteIolStock: (stockId: number) => del<{ ok: true }>(`/iol/stock/${stockId}`),
  iolMoves: (stockId: number) =>
    request<{ moves: IolMove[]; qty: number; batches: StockBatch[] }>(`/iol/moves${qs({ stock_id: stockId })}`),
  iolBatches: (stockId: number) => request<{ batches: StockBatch[] }>(`/iol/batches${qs({ stock_id: stockId })}`),
  createIolMove: (body: { stock_id: number; kind: "in" | "out"; qty: number; note: string; expiry?: string; batch_id?: number | null }) =>
    post<{ ok: true; qty: number; doc_id: number }>("/iol/moves", body),
  /* документы склада — приход и расход */
  stockDocs: (f: { kind: "in" | "out"; from?: string; to?: string; q?: string }) =>
    request<{ docs: StockDoc[] }>(`/iol/docs${qs({ kind: f.kind, from: f.from, to: f.to, q: f.q })}`),
  createStockDoc: (kind: "in" | "out", body: StockDocInput) =>
    post<{ doc_id: number; number: number; number_text: string }>("/iol/docs", { kind, ...body }),
  updateStockDoc: (docId: number, body: StockDocInput) => put<{ ok: true }>(`/iol/docs/${docId}`, body),
  deleteStockDoc: (docId: number) => del<{ ok: true }>(`/iol/docs/${docId}`),
  /** Поиск позиции для выбора — заказ, материалы пациента, расход дня. */
  /** kind — только линзы или только расходники; без него — всё, первые 40. */
  iolPositions: (q: string, kind?: "iol" | "material") =>
    request<{ positions: StockPosition[] }>(`/iol/positions${qs({ q, ...(kind ? { kind } : {}) })}`),
  /** Разделы «Приход» / «Расход» — все движения за период. */
  iolLedger: (f: { dir: "in" | "out"; from?: string; to?: string; q?: string }) =>
    request<{ moves: IolMove[] }>(`/iol/ledger${qs({ dir: f.dir, from: f.from, to: f.to, q: f.q })}`),
  stockOrders: (status: "expected" | "received" | "cancelled" | "all" = "expected") =>
    request<{ orders: StockOrder[] }>(`/iol/orders${qs({ status })}`),
  createStockOrder: (body: { supplier: string; note: string; expected_at: string; items: { stock_id: number; qty: number }[] }) =>
    post<{ order_id: number }>("/iol/orders", body),
  updateStockOrder: (orderId: number, body: { supplier: string; note: string; expected_at: string; items: { stock_id: number; qty: number }[] }) =>
    put<{ ok: true }>(`/iol/orders/${orderId}`, body),
  receiveStockOrder: (orderId: number, items?: { item_id: number; qty: number; expiry?: string }[], extNumber?: string) =>
    post<{ ok: true }>(`/iol/orders/${orderId}/receive`, { items, ext_number: extNumber }),
  cancelStockOrder: (orderId: number) => post<{ ok: true }>(`/iol/orders/${orderId}/cancel`),
  /* материалы пациента и опердня */
  entryMaterials: (entryId: number) => request<{ materials: EntryMaterial[] }>(`/op-lists/entries/${entryId}/materials`),
  addEntryMaterial: (entryId: number, body: { stock_id: number; qty: number; consume_now?: boolean }) =>
    post<{ mat_id: number; consumed: boolean }>(`/op-lists/entries/${entryId}/materials`, body),
  removeEntryMaterial: (entryId: number, matId: number) =>
    del<{ ok: true }>(`/op-lists/entries/${entryId}/materials/${matId}`),
  dayMaterials: (dayId: number) => request<{ materials: DayMaterial[] }>(`/op-lists/days/${dayId}/materials`),
  addDayMaterial: (dayId: number, body: { stock_id: number; qty: number; note: string }) =>
    post<{ move_id: number }>(`/op-lists/days/${dayId}/materials`, body),
  removeDayMaterial: (dayId: number, moveId: number) =>
    del<{ ok: true }>(`/op-lists/days/${dayId}/materials/${moveId}`),

  /* обновление системы — только администратор */
  updateStatus: (fetch = false) => request<UpdateStatus>(`/update/status${qs({ fetch: fetch ? 1 : undefined })}`),
  updateLog: () => request<{ log: string; running: boolean; state: string }>("/update/log"),
  startUpdate: () => post<{ started: true; pid: number }>("/update/start"),
  restartApp: () => post<{ ok: true; via: string }>("/update/restart"),

  /* журнал действий — только администратор */
  audit: (f: {
    page?: number;
    entity?: string;
    /** Несколько сущностей через запятую — журнал одного раздела. */
    entities?: string;
    entity_id?: number;
    user_id?: number;
    action?: string;
    q?: string;
  }) =>
    request<{ rows: AuditRow[]; page: number; perPage: number; total: number; pages: number }>(
      `/audit${qs(f)}`
    ),
  auditUsers: () =>
    request<{ users: { user_id: number; fio: string; login: string }[] }>("/audit/users"),

  /* per-field snippet templates */
  temps: (fieldId: string) =>
    request<{ temps: Temp[] }>(`/temps${qs({ field_id: fieldId })}`),
  /** Several snippet lists at once — the wizard needs a dozen of them to open. */
  tempsBulk: (fieldIds: string[]) =>
    request<{ temps: Record<string, Temp[]> }>(
      `/temps/bulk${qs({ field_ids: fieldIds.join(",") })}`
    ),
  createTemp: (fieldId: string, name: string, text: string, joinMode: JoinMode = "line") =>
    post<Temp>("/temps", { field_id: fieldId, name, text, join_mode: joinMode }),
  updateTemp: (tempId: number, name: string, text: string, joinMode: JoinMode) =>
    put<{ ok: true }>(`/temps/${tempId}`, { name, text, join_mode: joinMode }),
  deleteTemp: (tempId: number) => del<{ ok: true }>(`/temps/${tempId}`),
  sortTemps: (ids: number[]) => post<{ ok: true }>("/temps/sort", { ids }),

  /* администрирование — только is_admin, сервер проверяет сам */
  adminUsers: () => request<{ users: AdminUser[] }>("/admin/users"),
  createAdminUser: (body: Partial<AdminUser> & { password: string }) =>
    post<{ user_id: number }>("/admin/users", body),
  updateAdminUser: (userId: number, body: Partial<AdminUser> & { password?: string }) =>
    put<{ ok: true }>(`/admin/users/${userId}`, body),
  adminStats: (from: string, to: string) =>
    request<AdminStats>(`/admin/stats${qs({ from, to })}`),
  /* ИИ-помощник: модель, база знаний — только администратор */
  aiAdmin: () => request<AiAdmin>("/ai/admin"),
  aiSettings: (body: { enabled?: boolean }) => put<{ settings: AiAdmin["settings"] }>("/ai/admin/settings", body),
  aiRetrain: () => post<{ ok: true }>("/ai/admin/retrain"),
  aiAddUrl: (url: string, title: string, category: AiSource["category"]) =>
    post<{ source: AiSource }>("/ai/admin/sources", { url, title, category }),
  aiUpdateSource: (id: number, body: { title?: string; enabled?: boolean; category?: AiSource["category"] }) =>
    put<{ source: AiSource }>(`/ai/admin/sources/${id}`, body),
  aiReindex: (id: number) => post<{ source: AiSource }>(`/ai/admin/sources/${id}/reindex`),
  aiDeleteSource: (id: number) => del<{ ok: true }>(`/ai/admin/sources/${id}`),
  aiSearch: (q: string) => request<{ hits: (AiCitation & { text: string; score: number })[] }>(`/ai/admin/search${qs({ q })}`),
  aiTry: (docsId: string, text: string) =>
    post<{ fields: AiFormField[]; fill: AiFillResult | null; conclusion: AiConclusion }>("/ai/admin/try", {
      docs_id: docsId,
      text,
    }),
  /**
   * Книга в базу знаний — XHR, а не fetch: большой PDF грузится заметно
   * долго, и администратору нужна полоса загрузки.
   */
  aiUpload: (file: File, category: AiSource["category"], onProgress?: (share: number) => void) =>
    new Promise<{ source: AiSource }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `/api/ai/admin/sources/upload${qs({ category })}`);
      xhr.withCredentials = true;
      xhr.setRequestHeader("Content-Type", "application/octet-stream");
      xhr.setRequestHeader("X-File-Name", encodeURIComponent(file.name));
      xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
      xhr.onload = () => {
        let body: { error?: string; source?: AiSource } = {};
        try {
          body = JSON.parse(xhr.responseText);
        } catch {
          /* не JSON — ниже будет ошибка со статусом */
        }
        if (xhr.status >= 200 && xhr.status < 300 && body.source) resolve({ source: body.source });
        else reject(new ApiError(xhr.status, body.error ?? (xhr.status === 413 ? "файл слишком большой" : xhr.statusText)));
      };
      xhr.onerror = () => reject(new ApiError(0, "нет связи с сервером"));
      xhr.send(file);
    }),

  /* конструктор печатных форм: читают все, пишет администратор */
  customForms: () => request<{ forms: CustomFormSummary[] }>("/custom-forms"),
  customForm: (id: number) => request<{ form: CustomForm }>(`/custom-forms/${id}`),
  /** Исправленный в конструкторе вид встроенного документа (или null). */
  builtinForm: (docsId: string) =>
    request<{ form: CustomForm | null }>(`/custom-forms/builtin/${encodeURIComponent(docsId)}`),
  createCustomForm: (body: {
    name: string;
    body: string;
    docs_id?: string | null;
    show_forms: boolean;
    show_amb: boolean;
    show_stat: boolean;
  }) => post<{ form_id: number }>("/custom-forms", body),
  updateCustomForm: (
    id: number,
    body: { name: string; body: string; show_forms: boolean; show_amb: boolean; show_stat: boolean }
  ) => put<{ ok: true }>(`/custom-forms/${id}`, body),
  deleteCustomForm: (id: number) => del<{ ok: true }>(`/custom-forms/${id}`),
  /** Последние значения полей из документов пациента ({{doc:…}} при печати). */
  docFieldValues: (patId: number, fields: string[]) =>
    request<{ values: Record<string, string> }>(
      `/docs/field-values${qs({ pat_id: patId, fields: fields.join(",") })}`
    ),

  /* раздел «Запись» */
  /** Кабинеты клиники — и убранные (у прошлых записей кабинет с названием). */
  apptCabinets: () => request<{ cabinets: Cabinet[] }>("/appts/cabinets"),
  createApptCabinet: (body: { name: string; kind: CabinetKind }) =>
    post<{ cabinet_id: number }>("/appts/cabinets", body),
  updateApptCabinet: (cabinetId: number, body: { name?: string; kind?: CabinetKind }) =>
    put<{ ok: true }>(`/appts/cabinets/${cabinetId}`, body),
  orderApptCabinets: (ids: number[]) => put<{ ok: true }>("/appts/cabinets/order", { ids }),
  deleteApptCabinet: (cabinetId: number) => del<{ ok: true }>(`/appts/cabinets/${cabinetId}`),
  /** Окна приёма дня и заблокированное время. */
  apptWindows: (date?: string) =>
    request<{ windows: ApptWindow[]; blocks: ApptBlock[] }>(`/appts/windows${qs({ date })}`),
  /** Обычный интервал между пациентами: последнего приёма кабинета и вообще. */
  apptWindowsRecent: () =>
    request<{ by_cabinet: Record<number, number>; last: number }>("/appts/windows/recent"),
  createApptWindow: (body: ApptWindowInput) => post<{ win_id: number }>("/appts/windows", body),
  updateApptWindow: (winId: number, body: ApptWindowInput) => put<{ ok: true }>(`/appts/windows/${winId}`, body),
  deleteApptWindow: (winId: number) => del<{ ok: true }>(`/appts/windows/${winId}`),
  createApptBlock: (body: ApptBlockInput) => post<{ block_id: number }>("/appts/blocks", body),
  updateApptBlock: (blockId: number, body: ApptBlockInput) => put<{ ok: true }>(`/appts/blocks/${blockId}`, body),
  deleteApptBlock: (blockId: number) => del<{ ok: true }>(`/appts/blocks/${blockId}`),
  apptCalendar: (year: number, month: number) =>
    request<{
      windows: { d: string; n: number }[];
      appointments: { d: string; n: number }[];
      /** Раскладка по врачам на день — для клетки месяца «АВ: 3». */
      doctors: { d: string; doctor: string; n: number }[];
    }>(`/appts/calendar${qs({ year, month })}`),
  apptSettings: () =>
    request<{ doctor_initials: "io" | "fio"; slot_minutes: number }>("/appts/settings"),
  updateApptSettings: (body: { doctor_initials?: "io" | "fio"; slot_minutes?: number }) =>
    put<{ ok: true }>("/admin/settings", body),
  appts: (date: string, doctor?: string) =>
    request<{ appointments: Appointment[] }>(`/appts${qs({ date, doctor })}`),
  createAppt: (body: {
    cabinet_id: number;
    doctor: string;
    pat_id: number;
    date: string;
    time_from: string;
    time_to: string;
    comment: string;
    /** Обоснование записи вне открытого приёма — без него сервер не запишет. */
    override_reason?: string;
    /** Услуга, выбранная прямо при записи, — сразу в приём. */
    service_id?: number | null;
  }) => post<{ appt_id: number }>("/appts", body),
  updateAppt: (
    apptId: number,
    body: {
      cabinet_id: number;
      doctor: string;
      date: string;
      time_from: string;
      time_to: string;
      comment: string;
      override_reason?: string;
    }
  ) => put<{ ok: true }>(`/appts/${apptId}`, body),
  apptStatus: (apptId: number, status: ApptStatus) =>
    request<{ ok: true }>(`/appts/${apptId}/status`, { method: "PATCH", body: JSON.stringify({ status }) }),
  apptConfirmed: (apptId: number, confirmed: boolean) =>
    request<{ ok: true; confirmed: boolean }>(`/appts/${apptId}/confirmed`, {
      method: "PATCH",
      body: JSON.stringify({ confirmed }),
    }),
  apptPaid: (apptId: number, undo = false) =>
    request<{ ok: true; paid: boolean }>(`/appts/${apptId}/paid`, {
      method: "PATCH",
      body: JSON.stringify({ undo }),
    }),
  deleteAppt: (apptId: number) => del<{ ok: true }>(`/appts/${apptId}`),
  addApptService: (
    apptId: number,
    body: { service_id?: number | null; name?: string; price?: number; qty?: number }
  ) => post<{ item_id: number }>(`/appts/${apptId}/services`, body),
  removeApptService: (apptId: number, itemId: number) =>
    del<{ ok: true }>(`/appts/${apptId}/services/${itemId}`),
  services: () => request<{ services: Service[] }>("/appts/services"),
  createService: (name: string, price: number) =>
    post<{ service_id: number }>("/appts/services", { name, price }),
  updateService: (serviceId: number, name: string, price: number) =>
    put<{ ok: true }>(`/appts/services/${serviceId}`, { name, price }),
  /** Длительность услуги (настройки записи), мин; 0 — не задана. */
  setServiceDuration: (serviceId: number, minutes: number) =>
    put<{ ok: true }>(`/appts/services/${serviceId}`, { duration_min: minutes }),
  deleteService: (serviceId: number) => del<{ ok: true }>(`/appts/services/${serviceId}`),
};
